import React, { useState, useEffect } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuth } from '../../../app/providers';

type NavItem = {
  id: string;
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  exact?: boolean;
  badge?: number;
  submenu?: NavItem[];
};

// Icon components using inline SVG
const DashboardIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.2701 6.04039V0.681641H17.3181V6.04039H10.2701ZM0.681641 9.41914V0.681641H7.72964V9.41914H0.681641ZM10.2701 17.3181V8.58064H17.3181V17.3181H10.2701ZM0.681641 17.3181V11.9594H7.72964V17.3181H0.681641Z"
      fill="#5761EB"
    />
  </svg>
);

// const _HomeIcon = ({ className }: { className?: string }) => (
//   <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
//     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
//   </svg>
// )

const BuildingIcon = ({ className }: { className?: string }) => (
  <svg
    width="22"
    height="20"
    className={className}
    viewBox="0 0 22 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.702148 17.3184V8.2889L7.04065 3.77715L13.4954 8.37615C13.3416 8.55182 13.1914 8.72956 13.0449 8.9094C12.8984 9.08906 12.7482 9.26673 12.5944 9.4424L7.04065 5.5299L2.10115 9.00015V15.9194H4.38565V11.2704H9.6954V17.3184H8.2964V12.6694H5.78465V17.3184H0.702148ZM21.2981 0.702148V9.98765C21.0931 9.78698 20.8751 9.60507 20.6439 9.4419C20.4127 9.27873 20.1645 9.12681 19.8991 8.98615V2.10115H10.6521V4.02415L9.25315 3.0204V0.702148H21.2981ZM16.0751 5.9049H17.7434V4.23665H16.0751V5.9049ZM16.9409 19.2981C15.7317 19.2981 14.7027 18.8742 13.8539 18.0264C13.0052 17.1786 12.5809 16.1501 12.5809 14.9409C12.5809 13.7317 13.0048 12.7027 13.8526 11.8539C14.7005 11.0052 15.729 10.5809 16.9381 10.5809C18.1475 10.5809 19.1765 11.0048 20.0251 11.8526C20.8738 12.7005 21.2981 13.729 21.2981 14.9381C21.2981 16.1475 20.8742 17.1765 20.0264 18.0251C19.1786 18.8738 18.1501 19.2981 16.9409 19.2981ZM16.5174 17.8414H17.3819V15.3616H19.8616V14.5174H17.3819V12.0376H16.5174V14.5174H14.0376V15.3616H16.5174V17.8414Z"
      fill="#474747"
    />
  </svg>
);

const ChartIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.11056 17.2976C1.63323 17.2976 1.22948 17.1317 0.899309 16.7998C0.568975 16.4678 0.403809 16.0656 0.403809 15.5931V0.100586H1.80281V15.5908C1.80281 15.6678 1.83406 15.7384 1.89656 15.8026C1.95923 15.8666 2.03056 15.8986 2.11056 15.8986H17.6011V17.2976H2.11056ZM3.84631 14.0476V6.13609H6.64431V14.0476H3.84631ZM8.49531 14.0476V1.19659H11.2933V14.0476H8.49531ZM13.1443 14.0476V10.0958H15.9423V14.0476H13.1443Z"
      fill="#474747"
    />
  </svg>
);

const MegaphoneIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="16"
    viewBox="0 0 20 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.881 8.70911V7.31011H19.3955V8.70911H15.881ZM17.0032 15.2986L14.1915 13.2101L15.0425 12.0949L17.854 14.1834L17.0032 15.2986ZM15.004 3.86586L14.1532 2.75061L16.9647 0.662109L17.8155 1.77761L15.004 3.86586ZM3.37148 14.3189V10.4756H2.36998C1.88115 10.4756 1.46481 10.3049 1.12098 9.96361C0.77698 9.62228 0.60498 9.21053 0.60498 8.72836V7.29086C0.60498 6.80053 0.77698 6.38344 1.12098 6.03961C1.46481 5.69561 1.88198 5.52361 2.37248 5.52361H6.01573L10.355 2.93136V13.0679L6.01573 10.4756H4.77048V14.3189H3.37148ZM8.95598 10.5844V5.43511L6.41073 6.92261H2.31173C2.2349 6.92261 2.1644 6.95469 2.10023 7.01886C2.03606 7.08303 2.00398 7.15353 2.00398 7.23036V8.76886C2.00398 8.84569 2.03606 8.91619 2.10023 8.98036C2.1644 9.04453 2.2349 9.07661 2.31173 9.07661H6.41073L8.95598 10.5844ZM12.0425 11.0381V4.96111C12.4265 5.30794 12.7303 5.74286 12.954 6.26586C13.1778 6.78869 13.2897 7.36661 13.2897 7.99961C13.2897 8.63261 13.1778 9.21053 12.954 9.73336C12.7303 10.2564 12.4265 10.6913 12.0425 11.0381Z"
      fill="#474747"
    />
  </svg>
);

const BookIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.9379 14.8961C15.3504 14.8961 15.7004 14.7529 15.9879 14.4664C16.2754 14.1797 16.4191 13.8301 16.4191 13.4176C16.4191 13.0051 16.2759 12.6551 15.9894 12.3676C15.7029 12.0801 15.3534 11.9364 14.9409 11.9364C14.5282 11.9364 14.1781 12.0796 13.8906 12.3661C13.6031 12.6526 13.4594 13.0022 13.4594 13.4149C13.4594 13.8274 13.6027 14.1774 13.8894 14.4649C14.1759 14.7524 14.5254 14.8961 14.9379 14.8961ZM14.9384 17.8556C15.4362 17.8556 15.8976 17.7412 16.3224 17.5124C16.7472 17.2834 17.1019 16.9629 17.3864 16.5509C17.0204 16.3189 16.6319 16.1461 16.2209 16.0326C15.8101 15.9191 15.3836 15.8624 14.9416 15.8624C14.4996 15.8624 14.0724 15.9191 13.6599 16.0326C13.2474 16.1461 12.8597 16.3189 12.4969 16.5509C12.7771 16.9629 13.1296 17.2834 13.5544 17.5124C13.9792 17.7412 14.4406 17.8556 14.9384 17.8556ZM2.38839 17.2979C1.91739 17.2979 1.51522 17.1312 1.18189 16.7979C0.84839 16.4644 0.681641 16.0621 0.681641 15.5911V2.38839C0.681641 1.91739 0.84839 1.51522 1.18189 1.18189C1.51522 0.848391 1.91739 0.681641 2.38839 0.681641H15.6114C16.0824 0.681641 16.4846 0.848391 16.8179 1.18189C17.1514 1.51522 17.3181 1.91739 17.3181 2.38839V8.68064C17.0908 8.58581 16.8604 8.50472 16.6269 8.43739C16.3936 8.37006 16.1576 8.31464 15.9191 8.27114V2.38839C15.9191 2.31139 15.8871 2.24089 15.8229 2.17689C15.7589 2.11272 15.6884 2.08064 15.6114 2.08064H2.38839C2.31139 2.08064 2.24089 2.11272 2.17689 2.17689C2.11272 2.24089 2.08064 2.31139 2.08064 2.38839V15.5911C2.08064 15.6681 2.11272 15.7387 2.17689 15.8029C2.24089 15.8669 2.31139 15.8989 2.38839 15.8989H8.36564C8.40381 16.1541 8.45822 16.3974 8.52889 16.6289C8.59956 16.8604 8.68731 17.0834 8.79214 17.2979H2.38839ZM2.08064 14.8384V15.8989V2.08064V8.27114V8.19614V14.8384ZM4.35089 13.5134H8.44139C8.48489 13.2749 8.54414 13.0395 8.61914 12.8071C8.69414 12.5748 8.78231 12.3439 8.88364 12.1144H4.35089V13.5134ZM4.35089 9.68939H10.7846C11.175 9.36939 11.5887 9.09947 12.0259 8.87964C12.4631 8.65981 12.9336 8.49797 13.4374 8.39414V8.29039H4.35089V9.68939ZM4.35089 5.86514H13.6489V4.46614H4.35089V5.86514ZM14.9406 19.2546C13.7315 19.2546 12.7025 18.8307 11.8536 17.9829C11.005 17.1351 10.5806 16.1066 10.5806 14.8974C10.5806 13.6882 11.0046 12.6592 11.8524 11.8104C12.7002 10.9617 13.7287 10.5374 14.9379 10.5374C16.1472 10.5374 17.1762 10.9613 18.0249 11.8091C18.8736 12.657 19.2979 13.6855 19.2979 14.8946C19.2979 16.104 18.874 17.133 18.0261 17.9816C17.1783 18.8303 16.1498 19.2546 14.9406 19.2546Z"
      fill="#474747"
    />
  </svg>
);

const CogIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.69225 19.5L7.3115 16.4538C7.04367 16.3641 6.769 16.2385 6.4875 16.077C6.20617 15.9153 5.95459 15.7423 5.73275 15.5577L2.9115 16.75L0.604004 12.75L3.04425 10.9058C3.02125 10.7571 3.00492 10.6077 2.99525 10.4578C2.98559 10.3077 2.98075 10.1583 2.98075 10.0095C2.98075 9.86733 2.98559 9.72283 2.99525 9.576C3.00492 9.42917 3.02125 9.26858 3.04425 9.09425L0.604004 7.25L2.9115 3.26925L5.723 4.452C5.96417 4.261 6.22159 4.08633 6.49525 3.928C6.76892 3.76967 7.03784 3.64242 7.302 3.54625L7.69225 0.5H12.3078L12.6885 3.55575C12.9885 3.66475 13.2599 3.792 13.5028 3.9375C13.7458 4.083 13.991 4.2545 14.2385 4.452L17.0885 3.26925L19.396 7.25L16.9173 9.123C16.9531 9.2845 16.9727 9.4355 16.976 9.576C16.9792 9.71633 16.9808 9.85767 16.9808 10C16.9808 10.1358 16.9775 10.274 16.971 10.4145C16.9647 10.5548 16.9417 10.7154 16.902 10.8963L19.3615 12.75L17.0538 16.75L14.2385 15.548C13.991 15.7455 13.7384 15.9202 13.4808 16.072C13.2231 16.224 12.959 16.3481 12.6885 16.4443L12.3078 19.5H7.69225ZM9 18H10.9655L11.325 15.3212C11.8353 15.1879 12.3017 14.9985 12.724 14.753C13.1465 14.5073 13.5539 14.1916 13.9463 13.8057L16.4308 14.85L17.4155 13.15L15.2463 11.5155C15.3296 11.2565 15.3863 11.0026 15.4163 10.7537C15.4464 10.5051 15.4615 10.2538 15.4615 10C15.4615 9.73967 15.4464 9.48842 15.4163 9.24625C15.3863 9.00392 15.3296 8.75642 15.2463 8.50375L17.4345 6.85L16.45 5.15L13.9365 6.2095C13.6018 5.85183 13.2009 5.53583 12.7338 5.2615C12.2664 4.98717 11.7937 4.79292 11.3155 4.67875L11 2H9.0155L8.6845 4.66925C8.17434 4.78975 7.70325 4.97433 7.27125 5.223C6.83909 5.47183 6.42684 5.79233 6.0345 6.1845L3.55 5.15L2.5655 6.85L4.725 8.4595C4.64167 8.69683 4.58334 8.94367 4.55 9.2C4.51667 9.45633 4.5 9.72617 4.5 10.0095C4.5 10.2698 4.51667 10.525 4.55 10.775C4.58334 11.025 4.6385 11.2718 4.7155 11.5155L2.5655 13.15L3.55 14.85L6.025 13.8C6.4045 14.1897 6.81025 14.5089 7.24225 14.7578C7.67442 15.0064 8.152 15.1974 8.675 15.3307L9 18ZM10.0115 13C10.8435 13 11.5515 12.708 12.1355 12.124C12.7195 11.54 13.0115 10.832 13.0115 10C13.0115 9.168 12.7195 8.46 12.1355 7.876C11.5515 7.292 10.8435 7 10.0115 7C9.16917 7 8.45859 7.292 7.87975 7.876C7.30092 8.46 7.0115 9.168 7.0115 10C7.0115 10.832 7.30092 11.54 7.87975 12.124C8.45859 12.708 9.16917 13 10.0115 13Z"
      fill="#474747"
    />
  </svg>
);

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

// Admin/Manager navigation items
const adminNavItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    to: '/',
    icon: DashboardIcon,
    exact: true,
  },
  {
    id: 'house-management',
    label: 'House management',
    to: '/houses',
    icon: BuildingIcon,
    submenu: [
      {
        id: 'houses-overview',
        label: 'Overview',
        to: '/houses/overview',
        icon: BuildingIcon,
      },
      {
        id: 'manage-houses',
        label: 'Manage houses',
        to: '/houses',
        icon: BuildingIcon,
      },
      {
        id: 'manage-managers',
        label: 'Manage house managers',
        to: '/houses/managers',
        icon: BuildingIcon,
      },
      {
        id: 'manage-residents',
        label: 'Manage residents',
        to: '/houses/residents',
        icon: BuildingIcon,
      },
      {
        id: 'archive-residents',
        label: 'Archive residents',
        to: '/residents/archive',
        icon: BuildingIcon,
      },
    ],
  },
  {
    id: 'financials',
    label: 'Financials & reports',
    to: '/financials',
    icon: ChartIcon,
  },
  {
    id: 'announcements',
    label: 'House announcements',
    to: '/announcements',
    icon: MegaphoneIcon,
  },
  {
    id: 'resources',
    label: 'Resources',
    to: '/resources',
    icon: BookIcon,
  },
  {
    id: 'settings',
    label: 'Settings',
    to: '/settings',
    icon: CogIcon,
  },
];

const managerNavItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    to: '/',
    icon: DashboardIcon,
    exact: true,
  },
  {
    id: 'house-management',
    label: 'House management',
    to: '/houses',
    icon: BuildingIcon,
    submenu: [
      {
        id: 'houses-overview',
        label: 'Overview',
        to: '/houses/overview',
        icon: BuildingIcon,
      },
      {
        id: 'manage-houses',
        label: 'Manage houses',
        to: '/houses',
        icon: BuildingIcon,
      },
      {
        id: 'manage-residents',
        label: 'Manage residents',
        to: '/houses/residents',
        icon: BuildingIcon,
      },
      {
        id: 'archive-residents',
        label: 'Archive residents',
        to: '/residents/archive',
        icon: BuildingIcon,
      },
    ],
  },
  // {
  //   id: 'financials',
  //   label: 'Financials & reports',
  //   to: '/financials',
  //   icon: ChartIcon,
  // },
  {
    id: 'announcements',
    label: 'House announcements',
    to: '/announcements',
    icon: MegaphoneIcon,
  },
  {
    id: 'resources',
    label: 'Resources',
    to: '/resources',
    icon: BookIcon,
  },
  {
    id: 'settings',
    label: 'Settings',
    to: '/settings',
    icon: CogIcon,
  },
];

// Resident navigation items
const residentNavItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    // to: '/resident/dashboard',
    to: '/resident/dashboard',
    icon: DashboardIcon,
    exact: true,
  },
  // {
  //   id: 'exploreHouses',
  //   label: 'Explore Houses',
  //   to: '/resident/dashboard/houses',
  //   icon: BuildingIcon,
  //   exact: true,
  // },
  {
    id: 'payments',
    label: 'Payments',
    to: '/resident/dashboard/payments',
    icon: ChartIcon,
    exact: true,
  },
  {
    id: 'announcements',
    label: 'Announcements',
    to: '/resident/dashboard/announcements',
    icon: MegaphoneIcon,
  },
  {
    id: 'resource',
    label: 'Resource',
    to: '/resident/dashboard/resources',
    icon: BookIcon,
  },
  // {
  //   id: 'settings',
  //   label: 'Settings',
  //   to: '/settings',
  //   icon: CogIcon,
  // },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  // Auto-expand house management if we're on any of its routes
  const shouldExpandHouseManagement = () => {
    const houseManagementPaths = [
      '/houses',
      '/houses/managers',
      '/residents',
      '/residents/archive',
    ];
    return houseManagementPaths.some((path) => pathname === path);
  };

  const [expandedItems, setExpandedItems] = useState<string[]>(
    shouldExpandHouseManagement() ? ['house-management'] : [],
  );

  // Auto-expand house management when navigating to its routes
  useEffect(() => {
    if (
      shouldExpandHouseManagement() &&
      !expandedItems.includes('house-management')
    ) {
      setExpandedItems((prev) => [...prev, 'house-management']);
    }
  }, [pathname]);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  const isItemActive = (item: NavItem) => {
    // Handle resident dashboard
    if (
      item.to === '/resident/dashboard' &&
      pathname === '/resident/dashboard'
    ) {
      return true;
    }

    // Handle admin dashboard
    if (item.to === '/' && pathname === '/' && user?.role !== 'RESIDENT') {
      return true;
    }

    if (item.exact) {
      return pathname === item.to;
    }

    // For items with submenu, check if any submenu item is active
    if (item.submenu && item.submenu.length > 0) {
      return item.submenu.some((subItem) => isSubItemActive(subItem));
    }

    // For regular items, check if current path matches exactly
    if (item.to === '/') {
      return pathname === '/';
    }

    return pathname === item.to;
  };

  const isSubItemActive = (subItem: NavItem) => {
    if (subItem.exact) {
      return pathname === subItem.to;
    }

    // For sub-items, handle exact matches and nested paths properly
    if (subItem.to === '/houses') {
      // /houses should be active only for /houses, not /houses/managers
      return pathname === '/houses';
    }

    if (subItem.to === '/houses/managers') {
      // /houses/managers should be active for /houses/managers
      return pathname === '/houses/managers';
    }

    if (subItem.to === '/residents') {
      // /residents should be active only for /residents, not /residents/archive
      return pathname === '/residents';
    }

    if (subItem.to === '/residents/archive') {
      // /residents/archive should be active for /residents/archive
      return pathname === '/residents/archive';
    }

    // For other routes, use exact match
    return pathname === subItem.to;
  };

  const renderNavItem = (item: NavItem, isSubItem = false) => {
    const isActive = isSubItem ? isSubItemActive(item) : isItemActive(item);
    const isExpanded = expandedItems.includes(item.id);
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isDisabled = false; // All features are now enabled

    const baseClasses = `
      group flex items-center justify-between w-full rounded-lg transition-all duration-200 ease-out
      focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
      min-h-[44px] sm:min-h-[40px]
      ${
        isSubItem
          ? 'pl-11 pr-3 py-2 text-sm'
          : 'px-3 py-2.5 text-sm font-medium'
      }
      ${
        isActive
          ? 'bg-indigo-50 text-indigo-700 shadow-sm high-contrast:bg-indigo-200 high-contrast:text-indigo-900'
          : isDisabled
          ? 'text-gray-400 cursor-not-allowed'
          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 high-contrast:hover:bg-gray-200'
      }
      ${!isDisabled && !isActive ? 'hover:shadow-sm' : ''}
      high-contrast:border high-contrast:border-gray-600
    `;

    const content = (
      <>
        <div className="flex items-center gap-3">
          {!isSubItem && (
            <item.icon
              className={`w-4 h-4 flex-shrink-0 ${
                isActive
                  ? 'text-indigo-600'
                  : isDisabled
                  ? 'text-gray-400'
                  : 'text-gray-500 group-hover:text-gray-700'
              }`}
            />
          )}
          <span className="truncate text-lg">{item.label}</span>
          {item.badge && (
            <span className="ml-auto bg-indigo-100 text-indigo-600 text-xs font-medium px-2 py-0.5 rounded-full">
              {item.badge}
            </span>
          )}
        </div>
        {hasSubmenu && (
          <ChevronDownIcon
            className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            } ${isActive ? 'text-indigo-600' : 'text-gray-400'}`}
          />
        )}
      </>
    );

    if (hasSubmenu) {
      return (
        <div key={item.id}>
          <button
            onClick={() => toggleExpanded(item.id)}
            className={baseClasses}
            disabled={isDisabled}
          >
            {content}
          </button>
          {isExpanded && (
            <div className="mt-1 space-y-1">
              {item.submenu!.map((subItem) => renderNavItem(subItem, true))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.to}
        disabled={isDisabled}
        className={baseClasses}
        onClick={onClose}
      >
        {content}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[90] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Enhanced sidebar with accessibility */}
      <aside
        id="mobile-sidebar"
        className={`
          fixed lg:static inset-y-0 left-0 z-[100] w-80 bg-white border-r border-gray-200 
          transform transition-transform duration-300 ease-in-out lg:transform-none
          high-contrast:border-2 high-contrast:border-gray-600
          ${
            isOpen
              ? 'translate-x-0 shadow-2xl'
              : '-translate-x-full lg:translate-x-0'
          }
        `}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex flex-col h-full">
          {/* Logo/Header area */}
          <div className="flex items-center justify-between border-b border-gray-200 lg:border-b-0 p-4 lg:p-0">
            {/* Mobile-only Close Button */}
            <div className="lg:hidden flex justify-end w-full">
              <button
                onClick={onClose}
                className="p-2 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close navigation menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-2 space-y-2 overflow-y-auto">
            {(user?.role === 'RESIDENT'
              ? residentNavItems
              : user?.role === 'MANAGER'
              ? managerNavItems
              : adminNavItems
            ).map((item) => renderNavItem(item))}
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
